<template>
  <div
    :class="[bgClass, loaderClass, 'relative overflow-hidden']"
    :style="{ width: computedWidth, height: computedHeight }"
  >
    <!-- Added width and height styles dynamically -->
    <div
      class="shimmer absolute bottom-0 left-0 right-0 top-0"
      :style="shimmerStyle"
    ></div>
    <slot />
  </div>
</template>

<script lang="ts">
  // Constants remain the same
  const LOADER_TYPES = { rectangle: 'rectangle', circle: 'circle' }

  const LOADER_CSS_CLASSES = {
    [LOADER_TYPES.rectangle]: 'rounded',
    [LOADER_TYPES.circle]: 'rounded-full'
  }

  type LoaderTypesKeys = keyof typeof LOADER_TYPES
  type LoaderTypesValues = (typeof LOADER_TYPES)[LoaderTypesKeys]

  const SHIMMER_COLOR = '#ffffff'

  const isHexColor = (hexColor: string) => {
    const hex = hexColor.replace('#', '')

    return (
      typeof hexColor === 'string' &&
      hexColor.startsWith('#') &&
      hex.length === 6 &&
      !isNaN(Number('0x' + hex))
    )
  }

  const hexToRgb = (hex: string) => {
    const rgbArray = hex.match(/\w\w/g)?.map((x) => parseInt(x, 16))
    return rgbArray?.join(', ') || '255, 255, 255' // Default fallback to white
  }
</script>

<script setup lang="ts">
  import { computed, toRefs } from 'vue'

  const props = defineProps({
    type: {
      type: String as () => LoaderTypesValues,
      default: LOADER_TYPES.rectangle,
      validator(value: LoaderTypesValues) {
        return Object.values(LOADER_TYPES).includes(value)
      }
    },
    bgClass: {
      type: String,
      default: 'bg-gray-300'
    },
    cssClass: {
      type: String,
      default: ''
    },
    shimmerColor: {
      type: String,
      default: SHIMMER_COLOR
    },
    width: {
      type: [String, Number],
      default: '100%'
    },
    height: {
      type: [String, Number],
      default: '100%'
    }
  })

  // Destructure props
  const { type, bgClass, cssClass, shimmerColor, width, height } = toRefs(props)

  /**
   * Dynamic shimmerStyle based on the shimmerColor.
   */
  const shimmerStyle = computed(() => {
    const rgb = isHexColor(shimmerColor.value)
      ? hexToRgb(shimmerColor.value)
      : hexToRgb(SHIMMER_COLOR)
    return {
      backgroundImage: `linear-gradient(90deg, rgba(${rgb}, 0) 0%, rgba(${rgb}, 0.2) 20%, rgba(${rgb}, 0.5) 60%, rgba(${rgb}, 0))`
    }
  })

  /**
   * Determines the loader's CSS class based on its type or provided cssClass.
   */
  const loaderClass = computed(() => cssClass.value || LOADER_CSS_CLASSES[type.value])

  /**
   * Computed styles for width and height to dynamically handle props of different types (String/Number).
   */
  const computedWidth = computed(() =>
    typeof width.value === 'number' ? `${width.value}px` : width.value
  )

  const computedHeight = computed(() =>
    typeof height.value === 'number' ? `${height.value}px` : height.value
  )
</script>

<style scoped>
  .shimmer {
    transform: translateX(-100%);
    animation: shimmer 1.4s infinite;
  }

  @keyframes shimmer {
    100% {
      transform: translateX(100%);
    }
  }
</style>
